import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../../contexts/ThemeContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { useSystemHealthSummary } from '../../../system-health/useSystemHealthSummary';
import SyncLogTimeline from '../../../system-health/components/SyncLogTimeline';
import { systemHealthLinkPath } from '../../../system-health/utils';
import { skeletonPulseClass, viewAllLinkClass, widgetCardClass, widgetTitleClass } from '../../themeHelpers';
import { cn } from '../../../../lib/utils';

export interface RecentSyncActivityWidgetProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const RecentSyncActivityWidget: React.FC<RecentSyncActivityWidgetProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { colors } = useTheme();
  const { can } = useAuth();
  const healthLink = systemHealthLinkPath(can('system_health', 'edit'));
  const { data, isLoading, error } = useSystemHealthSummary();
  const cardClass = widgetCardClass(colors, embedded);
  const listClass = embedded
    ? 'flex-1 min-h-0 overflow-y-auto'
    : 'max-h-64 sm:max-h-80 overflow-y-auto';

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`${widgetTitleClass(colors)} mb-3`}>Recent sync activity</h2>
        <div className={`animate-pulse h-32 rounded-lg ${skeletonPulseClass(colors)}`} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cardClass}>
        <h2 className={widgetTitleClass(colors)}>Recent sync activity</h2>
        <p className={cn('text-sm mt-3', colors.textMuted)}>Unable to load sync activity.</p>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-3 flex-shrink-0 gap-2">
        <h2 className={widgetTitleClass(colors)}>Recent sync activity</h2>
        <Link to={healthLink} className={viewAllLinkClass(colors)}>
          System Health
        </Link>
      </div>
      <div className={listClass}>
        <SyncLogTimeline
          logs={data.emailSync.recentLogs}
          limit={effectiveLimit}
          variant="compact"
        />
      </div>
    </div>
  );
};

export default RecentSyncActivityWidget;
